<template>
  <div
    ref="chip"
    class="AiExpandableChip"
    :class="{
      'AiExpandableChip--menuOpen': menuOpen,
      'AiExpandableChip--menuOpened': animateClose,
      'AiExpandableChip--light': light,
    }"
    :style="{ width: menuWidth }"
    @mouseenter="openMenu('hover')"
    @mouseleave="closeMenu('hover')"
    @touchend="onChipTouchEnd">
    <ai-icon
      v-if="iconLeft"
      :name="iconLeft"
      :size="24"
      class="AiExpandableChip-icon AiExpandableChip-icon--left" />

    <ai-typo
      variant="paragraph-03-bold"
      as="span"
      class="AiExpandableChip-label">
      <slot name="label" />
    </ai-typo>

    <slot name="right">
      <ai-icon
        v-if="iconRight"
        :name="iconRight"
        :size="24"
        class="AiExpandableChip-icon AiExpandableChip-icon--right" />
    </slot>

    <div
      ref="menu"
      class="AiExpandableChip-menu"
      :class="{ 'AiExpandableChip-menu--open': menuOpen }">
      <slot name="menu" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { GenericIconName } from '../../atoms/AiIcon/types';

type MenuOpenMode = 'hover' | 'touch' | null;

type Props = {
  iconLeft?: GenericIconName;
  iconRight?: GenericIconName;
  expandOnHover?: boolean;
  light?: boolean;
};

defineProps<Props>();
const slots = useSlots();

const menuOpen = ref(false);
const animateClose = ref(false);
const menuOpenMode = ref<MenuOpenMode>(null);

const menu = ref<HTMLDivElement>();
const chip = ref<HTMLDivElement>();

const menuWidth = ref<string>('min-content');
const closedMenuWidth = ref<string>('');
const openedMenuWidth = ref<string>('');

const openMenu = (mode: MenuOpenMode) => {
  const sameMode = menuOpenMode.value === null || menuOpenMode.value === mode;
  if (!slots.menu || !sameMode) return;

  animateClose.value = false;
  menuOpen.value = true;
  menuWidth.value = openedMenuWidth.value;
  menuOpenMode.value = mode;
};

const closeMenu = (mode: MenuOpenMode) => {
  if (!slots.menu || mode !== menuOpenMode.value) return;

  animateClose.value = true;
  menuOpen.value = false;
  menuWidth.value = closedMenuWidth.value;
};

const onChipTouchEnd = () => {
  if (menuOpen.value) {
    closeMenu('touch');
  } else {
    openMenu('touch');
  }
};

onMounted(() => {
  if (!menu.value || !slots.menu || !chip.value) return;

  const { width: chipWidth } = chip.value.getBoundingClientRect();
  const { width } = menu.value.getBoundingClientRect();
  closedMenuWidth.value = `${chipWidth}px`;
  openedMenuWidth.value = `${width}px`;

  menuWidth.value = closedMenuWidth.value;
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/constants';

.AiExpandableChip {
  position: relative;
  cursor: default;
  user-select: none;
  background-color: colors.$neutral-900;
  box-shadow: 0px 6px 6px 0px #9b8c601a;
  padding: constants.$padding-00 constants.$padding-01;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-top-left-radius: 21px;
  border-top-right-radius: 21px;
  border-bottom-left-radius: 21px;
  border-bottom-right-radius: 21px;
  transition: width 100ms linear;

  &--light {
    background-color: colors.$neutral-100;
  }

  &--menuOpen {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &--menuOpened {
    transition: all 100ms linear 150ms;
  }
}

.AiExpandableChip-label {
  color: colors.$white;
  flex-grow: 1;
  text-align: left;

  .AiExpandableChip--light & {
    color: colors.$neutral-900;
  }
}

.AiExpandableChip-icon {
  color: white;

  &--left {
    margin-right: constants.$margin-00;
  }

  &--right {
    margin-left: constants.$margin-00;
  }

  .AiExpandableChip--light & {
    color: colors.$neutral-900;
  }
}

.AiExpandableChip-menu {
  position: absolute;
  z-index: 50;
  border-bottom-left-radius: 21px;
  border-bottom-right-radius: 21px;
  background-color: colors.$neutral-900;
  padding-left: constants.$padding-01;
  padding-right: constants.$padding-01;
  color: white;
  overflow: hidden;
  width: max-content;
  top: 95%;
  left: 0;
  max-height: 0;
  transition: all 200ms ease-out;

  &--open {
    max-height: 25vh;
    padding-bottom: constants.$padding-00;
  }
}
</style>
